import { Component, computed, signal, OnDestroy, OnInit, inject, LOCALE_ID } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { finalize, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OffersService } from '../../../../services/offers/offers.service';
import { Loading } from '../../../../utils/loading/loading';
import { PublicOffer } from './offers.model';

@Component({
  selector: 'app-offers',
  standalone: true,
  imports: [RouterLink, Loading],
  templateUrl: './offers.html',
  styleUrl: './offers.scss',
  animations: [
    trigger('slideAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(0%)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ position: 'absolute', top: 0, left: 0, width: '100%' }),
        animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(-100%)', opacity: 0 })),
      ]),
    ]),
  ],
})
export class Offers implements OnInit, OnDestroy {
  private offersService = inject(OffersService);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);
  private router = inject(Router);

  isArabic = computed(() => this.locale.startsWith('ar'));
  offers = signal<PublicOffer[]>([]);
  offerImages = signal<Record<string, string>>({});
  loading = signal(false);
  imagesLoading = signal(false);
  error = signal<string | null>(null);
  currentSlide = signal(0);
  currentOffer = computed(() => this.offers()[this.currentSlide()] ?? null);

  private autoplayInterval: any;

  ngOnInit() {
    this.loadOffers();
  }

  loadOffers() {
    this.loading.set(true);
    this.offersService
      .getPublicOffers({ pageSize: 10, orderDir: 'DESC' })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          this.offers.set(res.list);
          if (res.list.length > 0) {
            this.loadImages(res.list);
            this.startAutoplay();
          }
        },
        error: () => this.error.set(this.isArabic() ? 'حدث خطأ' : 'Failed to load offers'),
      });
  }

  private loadImages(offers: PublicOffer[]) {
    this.imagesLoading.set(true);

    const imageRequests = offers.map((offer) => {
      if (!offer.profileImageFid) {
        return of({ key: `${offer.id}_${offer.sourceType}`, url: '' });
      }

      return this.fileMgt.getImageUrl(offer.profileImageFid).pipe(
        map((img) => ({
          key: `${offer.id}_${offer.sourceType}`,
          url: (img.url as string) || '',
        })),
        catchError(() => of({ key: `${offer.id}_${offer.sourceType}`, url: '' })),
      );
    });

    forkJoin(imageRequests)
      .pipe(finalize(() => this.imagesLoading.set(false)))
      .subscribe({
        next: (results) => {
          const imageMap: Record<string, string> = {};
          results.forEach((item) => {
            imageMap[item.key] = item.url;
          });
          this.offerImages.set(imageMap);
        },
      });
  }

  getImage(offer: PublicOffer): string {
    const key = `${offer.id}_${offer.sourceType}`;
    return this.offerImages()[key] || '';
  }

  private startAutoplay() {
    this.autoplayInterval = setInterval(() => this.nextSlide(), 5000);
  }

  nextSlide() {
    if (this.offers().length > 0) {
      this.currentSlide.update((n) => (n + 1) % this.offers().length);
    }
  }

  previousSlide() {
    if (this.offers().length > 0) {
      this.currentSlide.update((n) => (n === 0 ? this.offers().length - 1 : n - 1));
    }
  }

  goToSlide(i: number) {
    this.currentSlide.set(i);
  }

  navigateToDetails(offer: PublicOffer) {
    if (offer.sourceType === 'OFFER') {
      this.router.navigate(['/user/offers/view-offer', offer.id]);
    }
    if (offer.sourceType === 'BUNDLE') {
      this.router.navigate(['/user/offers/view-bundle', offer.id]);
    }
  }

  ngOnDestroy() {
    if (this.autoplayInterval) clearInterval(this.autoplayInterval);
  }
}
